import React from 'react';
import { Modal } from 'antd';
import { useTranslation } from 'react-i18next';
import classes from './EntityInfoModal.module.scss';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { getTheme } from 'helpers/getTheme';
import cn from 'classnames';
import { countFileTypes, formatBytes, getDeclension } from 'helpers/selectedObjects';
import getDateWithTimeZone from 'helpers/getDateWithTimeZone';
import FileStatuses from 'components/FileStatuses/FileStatuses';
import { choosenDocument } from 'store/slices/dataDocumentsSlice';
import { setIsOpenInfoEntityModal } from 'store/slices/windowStateSlice';

type EntityInfoModalPops = {
  isOpen: boolean;
};

const EntityInfoModal = ({isOpen}: EntityInfoModalPops) => {
  const { choosenDocuments, selectedDocument, selectedRowKeys } = useAppSelector((store) => store.documents);
  const { userData } = useAppSelector((store) => store.userData);

  const dispatch = useAppDispatch();
  
  const { choosenFiles, choosenFolders } = React.useMemo(() => {
    const choosenFiles = choosenDocuments.filter(({ key }) => !(key as string).startsWith('folder'));
    const choosenFolders = choosenDocuments.filter(({ key }) => (key as string).startsWith('folder'));
    return { choosenFiles, choosenFolders };
  }, [choosenDocuments]);

  const choosenFolderIds = choosenFolders.map((folder) => String(folder.key)?.split(' ')[1]);
  
  const nestedCounts = choosenFolders.reduce((acc: {files: number, folders: number}, file: choosenDocument) => {
    if (choosenFolderIds.includes(String(file.parent_folder_id))) {
      return acc;
    }
    if (file.documents_amount) {
      acc.files += file.documents_amount
    }
    if (file.subfolders) {
      acc.folders += file.subfolders.length
    } 
    return acc;
  }, {files: 0, folders: 0});
  
  const choosenFilesLength = choosenFiles?.filter((file) => !choosenFolderIds.includes(String(file.folder_id)))?.length;

  const curEntity = selectedDocument as any; // IFolder | IDocument;
  const isBulkMode = selectedRowKeys.length > 1 && choosenDocuments.length > 1;
  const typeOfEntity = curEntity?.type === 'folder' ? 'folder' : 'file';

  const { hasFiles, hasFolders } = React.useMemo(() => choosenDocuments.reduce(
    (acc, { key }) => ({
      hasFiles: acc.hasFiles || !(key as string).startsWith('folder'),
      hasFolders: acc.hasFolders || (key as string).startsWith('folder'),
    }),
    { hasFiles: false, hasFolders: false }
  ), [choosenDocuments]);

  const bulkType = hasFiles && hasFolders ? 'mix' : hasFiles ? 'files' : 'folders';

  const needShowStatusesOfSimpleFile = curEntity?.is_new || curEntity?.signing_status !== null 
    || curEntity?.agreement_status !== null 

  const { t, i18n } = useTranslation();

  const filesSize = choosenDocuments.reduce((acc: number, file: choosenDocument) => {
    if (file.size) {
      return acc += file.size
    } 
    return acc;
  }, 0);

  const showSection: any = {
    true: {
      folders: (
        <div className={classes.infoWrap}>
          <div className={classes.infoTitle}>{t('Documents.modals.info.type')}</div><div className={classes.infoContent}>{t('Documents.modals.info.folders')}</div>
          {/* <div className={classes.infoTitle}>{t('Documents.modals.info.size')}</div><div className={classes.infoContent}>{formatBytes(filesSize, i18n.language)}</div> */}
          <div className={classes.infoTitle}>{t('Documents.modals.info.contain')}</div>
          <div className={classes.infoContent}>
            {getDeclension(nestedCounts.files + choosenFilesLength, [t('Documents.file1'), t('Documents.file2'), t('Documents.file3')])}{', '}
            {getDeclension(nestedCounts.folders || 0, [t('Documents.folder4'), t('Documents.folder2'), t('Documents.folder3')])}
          </div>
        </div>
      ),
      files: (
        <div className={classes.infoWrap}>
          <div className={classes.infoTitle}>{t('Documents.modals.info.tFiles')}</div><div className={classes.infoContent}>{countFileTypes(choosenDocuments)}</div>
          <div className={classes.infoTitle}>{t('Documents.modals.info.sFiles')}</div><div className={classes.infoContent}>{formatBytes(filesSize, i18n.language)}</div>
          <div className={classes.infoTitle}>{t('Documents.modals.info.nFiles')}</div>
          <div className={classes.infoContent}>{getDeclension(choosenDocuments?.length! || 0, [t('Documents.file1'), t('Documents.file2'), t('Documents.file3')])}</div>
        </div>
      ),
      mix: (
        <div className={classes.infoWrap}>
          <div className={classes.infoTitle}>{t('Documents.modals.info.type')}</div><div className={classes.infoContent}>{t('Documents.modals.info.mix')}</div>
          {/* <div className={classes.infoTitle}>{t('Documents.modals.info.sFiles')}</div><div className={classes.infoContent}>{formatBytes(filesSize, i18n.language)}</div> */}
          <div className={classes.infoTitle}>{t('Documents.modals.info.contain')}</div>
          <div className={classes.infoContent}>
            {getDeclension(nestedCounts.files + choosenFilesLength, [t('Documents.file1'), t('Documents.file2'), t('Documents.file3')])}{', '}
            {getDeclension(nestedCounts.folders || 0, [t('Documents.folder4'), t('Documents.folder2'), t('Documents.folder3')])}
          </div>
        </div>
      )
    },
    false: {
      folder: (
        <div className={classes.infoWrap}>
          <div className={classes.infoTitle}>{t('Documents.modals.info.nFolder')}</div><div className={classes.infoContent}>{curEntity?.name}</div>
          <div className={classes.infoTitle}>{t('Documents.modals.info.type')}</div><div className={classes.infoContent}>{t('Documents.modals.info.folder')}</div>
          {/* <div className={classes.infoTitle}>{t('Documents.modals.info.size')}</div><div className={classes.infoContent}>{formatBytes(curEntity?.size, i18n.language)}</div>
          <div className={classes.infoTitle}>{t('Documents.modals.info.createAt')}</div><div className={classes.infoContent}>{getDateWithTimeZone(curEntity?.upload_at, userData?.date_format!)}</div>
          <div className={classes.infoTitle}>{t('Documents.modals.info.updateAt')}</div><div className={classes.infoContent}>{getDateWithTimeZone(curEntity?.updated_at, userData?.date_format!)}</div> */}
          <div className={classes.infoTitle}>{t('Documents.modals.info.contain')}</div>
          <div className={classes.infoContent}>
            {getDeclension(curEntity?.documents_amount! || 0, [t('Documents.file1'), t('Documents.file2'), t('Documents.file3')])}{', '}
            {getDeclension(curEntity?.subfolders?.length || 0, [t('Documents.folder4'), t('Documents.folder2'), t('Documents.folder3')])}
          </div>
        </div>
      ),
      file: (
        <div className={classes.infoWrap}>
          <div className={classes.infoTitle}>{t('Documents.modals.info.nFile')}</div><div className={classes.infoContent}>{curEntity?.name}</div>
          <div className={classes.infoTitle}>{t('Documents.modals.info.tFile')}</div><div className={classes.infoContent}>{curEntity?.extension?.toLocaleUpperCase()}</div>
          <div className={classes.infoTitle}>{t('Documents.modals.info.sFile')}</div><div className={classes.infoContent}>{formatBytes(curEntity?.size, i18n.language)}</div>
          <div className={classes.infoTitle}>{t('Documents.modals.info.createAt')}</div><div className={classes.infoContent}>{getDateWithTimeZone(curEntity?.upload_at, userData?.date_format!)}</div>
          <div className={classes.infoTitle}>{t('Documents.modals.info.updateAt')}</div><div className={classes.infoContent}>{getDateWithTimeZone(curEntity?.updated_at, userData?.date_format!)}</div>
          {needShowStatusesOfSimpleFile && (<>
            <div className={classes.infoTitle}>{t('Documents.modals.info.status')}</div><div className={classes.infoContent}><FileStatuses file={curEntity!} simpleMode={true}/></div>
          </>)}
        </div>
      ),
    }
  };

  const onCancel = () => {
    dispatch(setIsOpenInfoEntityModal(false));
    // if (selectedDocument?.id) {
    //   dispatch(setIsOpenInfoEntityModal(false));
    // } else {
    //   dispatch(closeEntityModal({navigate}));
    // }
  }
  
  return (
    <Modal
      open={isOpen}
      width={'60%'}
      onCancel={onCancel}
      centered
      title={t('Documents.modals.info.title')}
      className={cn(getTheme(), classes.infoModal)}
      okButtonProps={{style: {display: 'none'}}}
      cancelText={t('Logs.logsModal.close')}
    >
      {showSection[String(isBulkMode)][isBulkMode ? bulkType : typeOfEntity]}
    </Modal>
  );
}

export default EntityInfoModal;
